import React, { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ServiceToggle from './ServiceToggle';
import { Upload, Image as ImageIcon, Check, Wand2, Cloud } from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import TabPageLayout, { PageLoader } from './TabPageLayout';

// Auto-pick a readable text color (near-black/white) for text on an accent background.
// Mirrors getContrastColor() in services-grid.html so the preview matches the live widget.
const getContrastText = (hex) => {
  try {
    const c = (hex || '').replace('#', '');
    const full = c.length === 3 ? c.split('').map((x) => x + x).join('') : c;
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    const lin = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
    const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    return L > 0.5 ? '#1a1a1a' : '#ffffff';
  } catch (e) { return '#ffffff'; }
};

const fetcher = async (url) => {
  const response = await apiV2.get(url);
  return response.data;
};

const COLOR_PRESETS = [
  { name: 'Blue', value: '#2563EB' },
  { name: 'Green', value: '#10B981' },
  { name: 'Purple', value: '#7C3AED' },
  { name: 'Red', value: '#DC2626' },
  { name: 'Orange', value: '#EA580C' },
  { name: 'Pink', value: '#DB2777' },
  { name: 'Teal', value: '#0D9488' },
  { name: 'Indigo', value: '#4F46E5' },
];

// Soft, borderless card surface that matches the dashboard aesthetic.
const Section = ({ title, description, children }) => (
  <div className="card-soft p-5 sm:p-6">
    <div className="mb-5">
      <h3 className="settings-section-title">{title}</h3>
      {description && <p className="settings-section-subtitle">{description}</p>}
    </div>
    {children}
  </div>
);

const BrandingTab = () => {
  const [uploading, setUploading] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const saveTimer = useRef(null);

  // Form state
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#2563EB');
  const [showPoweredBy, setShowPoweredBy] = useState(true);
  const [fontColor, setFontColor] = useState(''); // text on accent backgrounds; '' = auto-contrast

  const { data: orgData, isLoading, mutate } = useSWR(
    '/auth/organizations/me',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  useEffect(() => {
    if (orgData) {
      setLogoUrl(orgData.logoUrl || '');
      setPrimaryColor(orgData.primaryColor || '#2563EB');
      setShowPoweredBy(orgData.showPoweredByBookingFlows !== false);
      setFontColor(orgData.fontColor || '');
    }
  }, [orgData]);

  // --- Auto-save ---------------------------------------------------------
  const persist = async (payload) => {
    try {
      await apiV2.patch('/auth/organizations/me', payload);
      mutate();
      window.dispatchEvent(new Event('setupDataChanged'));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save changes');
    }
  };

  // Debounced save for the color/accent pickers (they fire rapidly while dragging).
  const persistColorsDebounced = (nextPrimary, nextFont) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persist({ primaryColor: nextPrimary, settings: { fontColor: nextFont || null } });
    }, 600);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be less than 2MB'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiV2.post('/auth/organizations/me/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogoUrl(response.data.logoUrl);
      toast.success('Logo uploaded');
      mutate();
      window.dispatchEvent(new Event('setupDataChanged'));
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      await apiV2.patch('/auth/organizations/me', { logoUrl: '' });
      setLogoUrl('');
      toast.success('Logo removed');
      mutate();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  if (isLoading) return <PageLoader />;

  const resolvedFontColor = fontColor || getContrastText(primaryColor);

  return (
    <TabPageLayout
      title="Branding"
      subtitle="Your logo, colors, and white-label settings for customer-facing pages"
      actions={
        <div
          data-testid="branding-autosave-status"
          className={`hidden sm:inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
            savedFlash ? 'text-green-600' : 'text-gray-400'
          }`}
        >
          {savedFlash ? <Check className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
          {savedFlash ? 'Saved' : 'Changes save automatically'}
        </div>
      }
    >
      <div className="space-y-5">
        {/* SECTION 1 — Logo */}
        <Section
          title="Logo"
          description="Appears on your booking widget, customer tickets, waivers, and emails"
        >
          <div className="space-y-4">
            {logoUrl ? (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-4">
                  <img src={logoUrl} alt="Your logo" className="h-14 w-auto max-w-[200px] object-contain" />
                  <span className="text-sm text-green-600 font-medium inline-flex items-center gap-1">
                    <Check className="w-4 h-4" /> Uploaded
                  </span>
                </div>
                <button
                  onClick={removeLogo}
                  data-testid="branding-remove-logo"
                  className="text-sm text-red-500 hover:text-red-600 font-medium"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <ImageIcon className="w-7 h-7 text-gray-300" />
                <span className="text-sm text-gray-400">No logo uploaded yet</span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/avif,image/heic,image/heif"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
                disabled={uploading}
              />
              <label
                htmlFor="logo-upload"
                data-testid="branding-upload-logo"
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer shadow-sm ${
                  uploading ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200'
                }`}
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading…' : logoUrl ? 'Change logo' : 'Upload logo'}
              </label>
              <span className="field-hint mt-0">PNG, JPG, WebP · Max 2MB · Saves instantly</span>
            </div>
          </div>
        </Section>

        {/* SECTION 2 — Colors (brand + accent text) */}
        <Section
          title="Colors"
          description="Your brand color drives buttons, pills, and highlights. The accent text color is what sits on top of them."
        >
          <div className="space-y-6">
            {/* Brand color */}
            <div>
              <Label className="field-label">Brand color</Label>
              <div className="flex items-center gap-3 flex-wrap">
                <Input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => { setPrimaryColor(e.target.value); persistColorsDebounced(e.target.value, fontColor); }}
                  className="h-11 w-12 cursor-pointer p-1 rounded-lg"
                  data-testid="branding-brand-color-picker"
                />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) { setPrimaryColor(val); if (val.length === 7) persistColorsDebounced(val, fontColor); }
                  }}
                  placeholder="#2563EB"
                  className="font-mono w-32 text-base"
                  maxLength={7}
                  data-testid="branding-brand-color-hex"
                />
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => { setPrimaryColor(preset.value); persistColorsDebounced(preset.value, fontColor); }}
                      className={`w-8 h-8 rounded-full transition-all hover:scale-110 ${
                        primaryColor.toLowerCase() === preset.value.toLowerCase()
                          ? 'ring-2 ring-offset-2 ring-gray-900'
                          : 'ring-1 ring-gray-200'
                      }`}
                      style={{ backgroundColor: preset.value }}
                      title={preset.name}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Accent text color */}
            <div>
              <Label className="field-label">Accent text color</Label>
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { id: '', label: 'Auto', icon: true },
                  { id: '#1a1a1a', label: 'Black' },
                  { id: '#ffffff', label: 'White' },
                ].map((opt) => {
                  const active = fontColor === opt.id;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => { setFontColor(opt.id); persistColorsDebounced(primaryColor, opt.id); }}
                      data-testid={`branding-font-color-${opt.label.toLowerCase()}`}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                        active ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:ring-gray-300'
                      }`}
                    >
                      {opt.icon && <Wand2 className="w-3.5 h-3.5" />}
                      {opt.label}
                    </button>
                  );
                })}
                <div className="flex items-center gap-2 ml-1">
                  <Input
                    type="color"
                    value={fontColor || resolvedFontColor}
                    onChange={(e) => { setFontColor(e.target.value); persistColorsDebounced(primaryColor, e.target.value); }}
                    className="h-10 w-11 cursor-pointer p-1 rounded-lg"
                    data-testid="branding-font-color-custom-picker"
                  />
                  <Input
                    type="text"
                    value={fontColor}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^#[0-9A-Fa-f]{0,6}$/.test(val)) { setFontColor(val); if (val === '' || val.length === 7) persistColorsDebounced(primaryColor, val); }
                    }}
                    placeholder="Auto"
                    className="font-mono w-28 text-base"
                    maxLength={7}
                  />
                </div>
              </div>
              <p className="field-hint">
                {fontColor
                  ? 'Using your custom color. Switch to Auto for automatic contrast.'
                  : `Auto picks black or white for best contrast (currently ${getContrastText(primaryColor)}).`}
              </p>
            </div>

            {/* Unified live preview */}
            <div className="rounded-2xl p-5 bg-gray-50">
              <p className="field-hint mt-0 mb-3">Live preview</p>
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm"
                  style={{ backgroundColor: primaryColor, color: resolvedFontColor }}
                  data-testid="branding-preview-button"
                >
                  Check availability
                </span>
                <span
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm"
                  style={{ backgroundColor: primaryColor, color: resolvedFontColor }}
                >
                  24 guests
                </span>
                <span className="text-sm font-medium" style={{ color: primaryColor }}>From $150</span>
              </div>
            </div>
          </div>
        </Section>

        {/* SECTION 3 — White Label */}
        <Section
          title="White label"
          description="Control the footer credit on your customer-facing pages"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex-1 pr-4">
                <Label className="text-sm font-medium text-gray-900">Enable white label</Label>
                <p className="field-hint">
                  {showPoweredBy
                    ? 'Currently showing: “Powered by BookingFlows”'
                    : `White-labeled: “© ${orgData?.name || 'Your Company'}”`}
                </p>
              </div>
              <span data-testid="branding-whitelabel-toggle">
                <ServiceToggle
                  checked={!showPoweredBy}
                  onChange={(val) => { setShowPoweredBy(!val); persist({ showPoweredByBookingFlows: !val }); }}
                />
              </span>
            </div>

            {/* Footer preview */}
            <div className="p-4 bg-white rounded-xl ring-1 ring-gray-100">
              <p className="field-hint mt-0 mb-2">Footer preview</p>
              <div className="flex items-center justify-center py-1.5">
                <span className="text-xs text-gray-400">
                  {showPoweredBy ? (
                    <>Powered by <span className="font-semibold" style={{ color: primaryColor }}>BookingFlows</span></>
                  ) : (
                    <>© {orgData?.name || 'Your Company'}</>
                  )}
                </span>
              </div>
            </div>

            {!showPoweredBy && (
              <p className="field-hint">
                The copyright links to your website. Set your website URL in <span className="font-medium text-gray-500">Settings → Profile</span>.
              </p>
            )}
          </div>
        </Section>
      </div>
    </TabPageLayout>
  );
};

export default BrandingTab;
